// src/TaskAssignment.js
import React from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Tooltip,
  Box,
  Typography,
} from '@mui/material';
import api, { API_BASE } from './apiClient';
import { useSnackbar } from 'notistack';

const getUploadsBase = () => {
  if (API_BASE && API_BASE.startsWith('http')) return API_BASE;
  if (api?.defaults?.baseURL && /^https?:/i.test(api.defaults.baseURL)) return api.defaults.baseURL;
  return window.location.origin;
};

const buildUserDisplay = (user = {}) => {
  const firstName =
    user.first_name || user.firstname || user.firstName || user.username || user.email || '';
  const lastName = user.last_name || user.lastname || user.lastName || '';
  const username = user.username || user.email || '';
  const fullName = `${(firstName || '').trim()} ${(lastName || '').trim()}`.trim() || username || 'User';
  const words = (firstName || '').trim().split(/\s+/).filter(Boolean);
  let initials = '';
  if (firstName && lastName) initials = `${firstName[0]}${lastName[0]}`;
  else if (words.length >= 2) initials = `${words[0][0]}${words[1][0]}`;
  else if (words.length === 1) initials = words[0][0];
  else if (username) initials = username[0];
  initials = initials.toUpperCase();
  const avatarSrc =
    user.avatar ||
    user.avatar_url ||
    user.profile_pic ||
    user.profilePic ||
    user.photo_url ||
    (user.photo ? `${getUploadsBase()}/uploads/${user.photo}` : null) ||
    null;
  return { firstName: firstName || username || 'User', fullName, initials, avatarSrc };
};

const avatarPalette = ['#00897B', '#2563EB', '#7C3AED', '#EA580C', '#0EA5E9', '#E11D48', '#0F766E'];
const pickAvatarColor = (seed = '') => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return avatarPalette[hash % avatarPalette.length];
};

const UserAvatar = ({ user, size = 24 }) => {
  const data = buildUserDisplay(user || {});
  const bg = pickAvatarColor(data.fullName || data.initials || 'u');
  return (
    <Tooltip title={data.fullName} placement="top">
      <Avatar
        src={data.avatarSrc || undefined}
        sx={{ width: size, height: size, fontSize: size * 0.45, bgcolor: bg }}
      >
        {!data.avatarSrc && data.initials}
      </Avatar>
    </Tooltip>
  );
};

function TaskAssignment({ taskId, assignedTo, users, onAssign, currentUserId, compact = false }) {
  const { enqueueSnackbar } = useSnackbar();

  const handleChange = async (e) => {
    try {
      await api.put(`/api/tasks/${taskId}/assign`, { assigned_to: e.target.value, user_id: currentUserId });
      enqueueSnackbar('Task assigned', { variant: 'success' });
      onAssign(e.target.value);
    } catch (err) {
      enqueueSnackbar('Error assigning task', { variant: 'error' });
    }
  };

  return (
    <FormControl
      fullWidth={!compact}
      size="small"
      sx={compact ? { minWidth: 96, maxWidth: 120 } : {}}
    >
      <InputLabel>Assigned To</InputLabel>
      <Select
        value={assignedTo || ''}
        onChange={handleChange}
        renderValue={(value) => {
          if (!value) return 'None';
          const u = users.find((usr) => Number(usr.id) === Number(value)) || { username: `User ${value}` };
          return <UserAvatar user={u} size={28} />;
        }}
        sx={
          compact
            ? {
                minWidth: 96,
                maxWidth: 120,
                fontSize: 12,
                '& .MuiSelect-select': { padding: '6px 8px' },
              }
            : {}
        }
      >
        <MenuItem value="">
          <em>None</em>
        </MenuItem>
        {users.map((u) => (
          <MenuItem key={u.id} value={u.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <UserAvatar user={u} size={24} />
              <Typography variant="body2">{buildUserDisplay(u).firstName}</Typography>
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export default TaskAssignment;
